<template>
    <div>
        <div
            :id="sectionAnchor"
            class="px-4 flex-col widescreen:px-0 tablet:pt-20 desktop:pt-24 flex justify-center z-10"
            :class="bgColor">

            <div class="z-20 flex justify-center smMobile:pt-4 smMobile:pb-4 desktop:pb-8 desktop:pt-0 text-navy-dark font-bold tablet:text-lg desktop:text-xl">
                <span class="text-center">Call to order: <a href="tel:888-684-6595" class="text-navy-dark underline hover:text-blue">1 888-684-6595</a></span>
            </div>
            <div
                class="relative container desktop:rounded-md p-0 desktop:pb-2 desktop:px-4"
                :class="toggleBorder == true ? 'desktop:pt-24 desktop:border-4 desktop:border-blue-light' : 'desktop:pt-16'"
                >
                <div v-if="showOfferCards"
                    class="text-center text-navy-dark desktop:px-6 desktop:absolute desktop:top-0 desktop:left-1/2 desktop:transform desktop:-translate-x-1/2 desktop:-translate-y-1/2 mb-12 desktop:mb-0"
                    :class="bgColor">
                    <h3 class="desktop:whitespace-nowrap">
                        {{ heading }}
                    </h3>
                    <p>
                        {{ offerMostPopularText }}
                    </p>

                    <span v-if="getCurrentAddressAsString(this)" class="relative top-1 text-center text-sm">
                        <strong class="text-salmon">Current Location</strong>
                        <span class="ml-2">{{ getCurrentAddressAsString(this) }}</span>
                        <a @click="changeLocation()" class="track underline font-bold ml-2" data-category="Verify Address not into buyflow">Change?</a>
                    </span>
                    <span v-else class="text-center">
                        <a @click="changeLocation()" class="text-sm underline font-bold">Check Availability?</a>
                    </span>
                </div>

                <div v-else class="text-center mb-10">
                    <h3 class="text-xl tablet:text-4xl text-navy-dark">
                        Check Availability
                    </h3>
                    <p>
                        Enter your ZIP Code so that we can check if service is
                        available in your area.
                    </p>
                </div>
                <p
                    v-if="errorMessage && errorMessage.length"
                    class="text-white text-center text-sm bg-salmon p-4 mb-4"
                    v-html="errorMessage"
                >
                </p>

                <div v-if="showOfferCards && togglePricing"
                    class="grid grid-cols-1 justify-center grid-flow-row gap-4 desktop:gap-2 mb-4 tablet:mb-9 desktop:mb-8"
                    style="grid-template-columns: repeat(auto-fit, minmax(20rem, max-content));"
                    >
                    <BusinessOfferCard
                        v-for="(offer, index) in offers"
                        :key="index"
                        :ctaText="offerCtaText"
                        :offerLinkLabel="offerLinkLabel"
                        :offerLinkUrl="offerLinkUrl"
                        v-bind="attachAdditionalPropsTo(offer)"
                        :displayPrice="togglePricing"
                        :mostPopular="offer.name.toLowerCase() === 'Unlimited'"
                        :webForm="webForm"
                        @on-click="selectOffer(offer)"
                        @term-click="selectTerms"
                        layout="card"
                    />
                </div>
                <div
                    v-else-if="showOfferCards && !togglePricing"
                    class="grid grid-cols-1 justify-center grid-flow-row gap-4 desktop:gap-2 mb-4 tablet:mb-9 desktop:mb-8"
                    style="grid-template-columns: repeat(auto-fit, minmax(20rem, max-content));"
                >
                    <BusinessOfferCard
                        v-for="(offer, index) in offers"
                        :key="index"
                        :ctaText="offerCtaText"
                        v-bind="attachAdditionalPropsTo(offer)"
                        :displayPrice="togglePricing"
                        :webForm="webForm"
                        @on-click="selectOffer(offer)"
                        @term-click="selectTerms"
                        layout="card"
                    />
                </div>

                <!-- Toggle for showing/hiding the Broadband Labels -->
                <div v-if="showOfferCards" class="text-center mb-8">
                    <span class="mr-2 font-light">Show/Hide Broadband Labels</span>
                    <ToggleButton
                        v-model="showBroadbandLabels"
                        :width="42"
                        :height="24"
                        :value="true"
                        color="#0057B8"
                    />
                </div>

                <!-- Mobile speed selector -->
                <fieldset v-if="showBroadbandLabels" class="block desktop:hidden border-0 p-0 m-0 text-center">
                    <div class="font-bold rounded-full inline-block bg-blue-polar-light">
                        <span v-for="(offer, index) in offers" :key="index" class="speed-select inline-block relative">
                            <input 
                                class="appearance-none opacity-min bg-none absolute top-0 w-full z-10 border-2 border-solid h-full left-0" 
                                type="radio" 
                                v-model="selectedProductIndex" 
                                :value="index" 
                                :id="'r1'" 
                                name="radios" 
                                :checked="offer.name === 'Business Essential'">
                            <label 
                                class="block py-0.5 px-5 rounded-full border-2 border-solid border-transparent text-navy-dark" 
                                for="r1">
                                {{offer.speed.download}}<br>Mbps
                            </label>
                        </span>
                    </div>
                </fieldset>

                <!-- Labels error state - single warning for all labels -->
                <div 
                    v-if="showOfferCards && showBroadbandLabels && !labelAvailable && !labelsAreLoading" 
                    class="max-w-4xl mx-auto mb-8 p-6 text-center bg-red-50 border border-red-200 rounded-lg"
                >
                    <h3 class="text-red-700 font-semibold mb-2">
                        We are experiencing technical difficulties.
                    </h3>
                    <p class="text-red-600">
                        Please visit your MyAccount online portal later to view a label describing key features of your Breezeline's service option.
                    </p>
                </div>

                <!-- Internet only offers labels grid -->
                <div 
                    v-else-if="showOfferCards && type === 'internet' && showBroadbandLabels" 
                    class="grid grid-cols-1 justify-center grid-flow-row tablet:gap-4 desktop:gap-2" 
                    style="grid-template-columns: repeat(auto-fit, minmax(20rem, max-content));"
                >
                    <BusinessLabel
                        v-for="(offer, index) in offers" 
                        :key="index"
                        :offerTitle="offer.name"
                        :showBroadbandLabels="showBroadbandLabels"
                        :selectedLabel="offers[selectedProductIndex].name"
                        v-bind="offer"
                        :download="offer.speed.download"
                        :upload="offer.speed.upload"
                        :contractTerms="offer.termDisclaimer"
                        :contractLength="offer.terms[0].type"
                        :columns=1
                        :hideWarning="true"
                    />
                </div>

                <!-- Bundle offers label -->
                <div 
                    v-else-if="showOfferCards && internetOffer && showBroadbandLabels" 
                    class="grid grid-cols-1 justify-center grid-flow-row tablet:gap-4 desktop:gap-2" 
                    style="grid-template-columns: repeat(auto-fit, minmax(20rem, max-content));"
                >
                    <BusinessLabel
                        :offerTitle="internetOffer.name"
                        :showBroadbandLabels="showBroadbandLabels"
                        :selectedLabel="internetOffer.name"
                        v-bind="internetOffer"
                        :download="internetOffer.speed.download"
                        :upload="internetOffer.speed.upload"
                        :contractTerms="internetOffer.termDisclaimer"
                        :contractLength="internetOffer.terms[0].type"
                        :columns=1
                        :hideWarning="true"
                    />
                </div>

                <div v-if="showOfferCards"
                    class="text-sm text-center text-navy-dark mt-8 px-4">
                    <p>Prices guaranteed with three-year contract. | *includes $10 Autopay discount.</p>
                    <p v-if="dynamicDisclaimer">{{dynamicDisclaimer}}</p>
                    <a class="text-navy-dark underline hover:text-blue" :href="promoLink" target="_blank">See Service & Promotion Details</a> -
                    <a class="text-navy-dark underline hover:text-blue" :href="termsLink" target="_blank">Terms and Conditions</a>

                    <!-- <div v-if="getCurrentAddressAsString(this)"
                         class="text-center flex flex-col pt-4 text-sm">
                        <strong class="text-salmon">
                            Current Location
                        </strong>
                        <p>
                            {{ getCurrentAddressAsString(this) }}
                        </p>
                        <a @click="changeLocation()"
                           class="track underline font-bold"
                           data-category="Verify Address not into buyflow">
                            Change?
                        </a>
                    </div>
                    <div v-else class="text-center pt-4">
                        <a @click="changeLocation()"
                           class="text-sm underline font-bold">
                            Check Availability?
                        </a>
                    </div> -->
                </div>
                <CheckAvailabilityZip v-else @set-errors="setErrors" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import BusinessOfferCard from "@/molecules/BusinessOfferCard";
import CheckAvailabilityZip from "@/organisms/CheckAvailabilityZip";
import BusinessLabel from '@/organisms/BroadbandLabel/BusinessLabel';
import { ToggleButton } from 'vue-js-toggle-button';

export default {
    name: "BusinessOfferCards",
    components: {
        CheckAvailabilityZip,
        BusinessOfferCard,
        ToggleButton,
        BusinessLabel
    },
    props: {
        heading: {
            type: String,
            required: true,
        },
        offerMostPopularText: {
            type: String,
            required: false,
            default: "Our most popular offer",
        },
        offerCtaText: {
            type: String,
            required: false,
        },
        offerLinkLabel: {
            type: String,
            required: false,
        },
        offerLinkUrl: {
            type: String,
            required: false,
        },
        promoLabel: {
            type: String,
            default: null,
        },
        promoLink: {
            type: String,
            default: null,
        },
        dynamicDisclaimer: {
            type: String,
            default: null,
        },
        reRoute: {
            type: String,
            required: false,
            default: () => [],
        },
        sectionAnchor: {
            type: String,
            required: false,
        },
        sectionColor: {
            type: String,
            required: false,
        },
        termsLink: {
            type: String,
            default: null
        },
        togglePricing: {
            type: Boolean,
            default: false,
            required: false,
        },
        toggleBorder: {
            type: Boolean,
            default: false,
            required: false,
        },
        type: {
            type: String,
            required: false,
            default: 'internet',
        },
        webForm: {
            required: false,
        },
    },
    data: () => ({
        errorMessage: null,
        showOfferCards: false,
        showBroadbandLabels: true,
        selectedProductIndex: 0,
    }),
    computed: {
        ...mapState([
            "businessSelectPlanModal", 
            "businessOffers", 
            "meta"
        ]),
        ...mapState('BroadbandLabels', [
            'labelsAreLoading',
            'labelAvailable'
        ]),
        ...mapGetters(['getCurrentAddressAsString']),
        offers(){
            if(Object.keys(this.businessOffers).length > 0){
                return this.businessOffers.products.filter(offer => offer.type === this.type).sort((a, b) => (a.monthlyCharge > b.monthlyCharge) ? 1 : -1)
            }
            return []
        },
        internetOffer(){
            if(Object.keys(this.businessOffers).length > 0){
                return this.businessOffers.products.filter(offer => offer.type === 'internet').sort((a, b) => (a.monthlyCharge > b.monthlyCharge) ? 1 : -1)[0]
            }
            return null
        },
        cookiePromotion(){
          return this.meta.businessPromotion? this.meta.businessPromotion.name : null
        },
        mostPopular() {
            return this.businessOffers.mostPopularProduct;
        },
        reRouteItems() {
            return JSON.parse(this.reRoute)[0];
        },
        bgColor() {
            const colVal = this.sectionColor.slice(-3)
            const formatString = this.sectionColor.replace(/(?=[A-Z])/, '-').toLowerCase()

            if (this.sectionColor === `gray${colVal}`) {
                return `bg-gray-${colVal}`
            } else {
                return `bg-${formatString}`
            }
        }
    },
    watch: {
        businessOffers(value){
            this.showOfferCards = this.offers.length > 0
        }
    },
    created() {
        let fiber1 = ["biz_host_rdirect", "biz_host_fiber"];
        let fiber2 = ["biz_fiber_solution", "biz_fiber_solution"];
        let result =
            fiber1.every((x) => this.meta.features?.indexOf(x) > -1) ||
            fiber2.every((x) => this.meta.features?.indexOf(x) > -1);

        if (result) {
            window.location = "/business/enterprise/fiber-solutions";
        }
    },
    mounted() {
        this.checkTier();

        if (Object.keys(this.businessOffers).length > 0) {
            this.showOfferCards = this.offers.length > 0
        }
    },
    updated() {
        this.checkTier();
    },
    methods: {
        ...mapActions(["toggleBusinessSelectPlanModal", "toggleSlottedModal", "toggleCheckAvailabilityModal"]),
        setErrors(error) {
            this.errorMessage = error;
        },
        setOffers(offers){
            this.offers = offers.products.filter(offer => offer.type === this.type).sort((a, b) => (a.monthlyCharge > b.monthlyCharge) ? 1 : -1)
        },
        changeLocation() {
            this.toggleCheckAvailabilityModal({
                visible: true,
                routeToBuyflow: false,
                resetSelectedOffer: true,
                currentCustomerCheck: false,
            })
        },
        hasBanner(offer) {
            if (this.meta.stateOrProvince === 'OH'){
                if (offer.name === 'Business Plus') {
                    return 'border-4 border-salmon'
                }
            } else if (offer.name === 'Business Essential') {
                return 'border-4 border-teal'
            }
            return ''
        },
        attachAdditionalPropsTo(offer) {
            offer.mostPopularText = this.offerMostPopularText;
            offer.ctaText = this.offerCtaText;
            offer.offerLink = this.offerLink;
            offer.hasBanner = this.hasBanner(offer)
            return offer;
        },
        selectOffer(offer) {
            this.toggleBusinessSelectPlanModal({
                visible: true,
                confirmation: false,
                offer: offer,
            });
        },
        selectTerms(terms) {
            this.toggleBusinessSelectPlanModal({
                visible: true,
                termsValues: terms,
            });
        },
        checkTier() {
            if (
                this.reRouteItems !== undefined &&
                this.reRouteItems.enable === "true"
            ) {
                this.getCookieValues();
                if (
                    this.cookiePromotion === "Tier-0 Tier-2" ||
                    this.cookiePromotion === "Tier-1"
                ) {
                    window.location.href = this.reRouteItems.url;
                }
            }
        },
        filterDownloadSpeed(speed){
            if (speed.includes(',')) {
                return speed.split(',')[1];
            } else {
                return speed;
            }
        },
    },
};
</script>

<style scoped>
fieldset.block {
    margin-bottom: 5px;
}

.label-wrapper {
    display: grid;
    transition: all 0.3s ease;
}

.broadband-label-item {
    order: 1;
    transition: order 0.3s ease;
}

.label-wrapper.selected-0 .label-index-0,
.label-wrapper.selected-1 .label-index-1,
.label-wrapper.selected-2 .label-index-2,
.label-wrapper.selected-3 .label-index-3 {
    order: 0;
}

/* Error state styles */
.text-red-600 {
    color: rgb(220, 38, 38);
}

.text-red-700 {
    color: rgb(185, 28, 28);
}

.bg-red-50 {
    background-color: rgb(254, 242, 242);
}

.border-red-200 {
    border-color: rgb(254, 202, 202);
}

/* Mobile styles (1023px and below) */
@media (max-width: 1023px) {
    .label-wrapper .broadband-label-item {
        display: none;
    }

    /* Show the first item when none is selected */
    .label-wrapper.selected--1 .label-index-0,
    .label-wrapper.selected-0 .label-index-0,
    .label-wrapper.selected-1 .label-index-1,
    .label-wrapper.selected-2 .label-index-2,
    .label-wrapper.selected-3 .label-index-3 {
        display: block;
    }
}

/* Desktop styles (1024px and above) */
@media (min-width: 1024px) {
    .label-wrapper {
        grid-template-columns: repeat(var(--columns, 4), 1fr);
    }
}
</style>